import React, { useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';

const PLAQUE_STATUS = [
  'None',
  'Minimal',
  'Moderate',
  'Severe',
  'Not Well Visualized'
];

const WAVEFORM_OPTIONS = [
  'Triphasic/Biphasic',
  'Monophasic',
  'High Velocity with Spectral Broadening',
  'Absent'
];

const INITIAL_AORTA_DATA = {
  examType: 'Aorta',
  examDate: new Date().toISOString().split('T')[0],
  firstName: '',
  lastName: '',
  mrn: '',
  technologist: '',
  reason: '',
  callReport: false,
  phoneNumber: '',
  history: '',
  historyOfSmoking: false,
  familyHistory: false,
  hypertension: false,
  comments: '',
  aorta: {
    plaque: 'None',
    peakSystolicVelocity: '',
    waveform: 'Triphasic/Biphasic',
    proximal: '',
    mid: '',
    distal: ''
  },
  iliacVessels: {
    rightIliac: '',
    leftIliac: ''
  }
};

function AortaForm({ token, onBack, facilityInfo }) {
  // Fetch dynamic technologists list
  const { technologists, defaultTech } = useTechnologists();
  const {
    formData,
    updateField,
    updateNestedField,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    resetForm,
    isLoading,
    error,
    success,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(
    {
      ...INITIAL_AORTA_DATA,
      technologist: defaultTech
    },
    'Aorta',
    {
      token, facilityInfo
    }
  );

  // Update technologist field when defaultTech loads
    useEffect(() => {
      if (defaultTech && !formData.technologist) {
        updateField('technologist', defaultTech);
      }
    }, [defaultTech, formData.technologist, updateField]);

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={hookHandleSubmit}>
        {/* Status Messages */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {/* Draft restore banner */}
        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        {/* History */}
        <div className="form-section">
          <h3>History</h3>
          <div className="form-group">
            <label>Clinical History:</label>
            <input
              type="text"
              value={formData.history}
              onChange={(e) => updateField('history', e.target.value)}
              placeholder="Enter clinical history"
            />
          </div>
          
          <div className="form-group" style={{ display: 'flex', gap: '20px' }}>
            <label>
              <input
                type="checkbox"
                checked={formData.historyOfSmoking}
                onChange={(e) => updateField('historyOfSmoking', e.target.checked)}
              />
              {' '}History of Smoking
            </label>
            <label>
              <input
                type="checkbox"
                checked={formData.familyHistory}
                onChange={(e) => updateField('familyHistory', e.target.checked)}
              />
              {' '}Family History
            </label>
            <label>
              <input
                type="checkbox"
                checked={formData.hypertension}
                onChange={(e) => updateField('hypertension', e.target.checked)}
              />
              {' '}Hypertension
            </label>
          </div>
        </div>

        {/* Aorta Measurements */}
        <div className="form-section">
          <h3>Aorta</h3>
          
          <div className="form-group">
            <label>Plaque:</label>
            <select
              value={formData.aorta.plaque}
              onChange={(e) => updateNestedField('aorta.plaque', e.target.value)}
            >
              {PLAQUE_STATUS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Peak Systolic Velocity (cm/sec):</label>
            <input
              type="number"
              step="0.1"
              placeholder="Measurement"
              value={formData.aorta.peakSystolicVelocity}
              onChange={(e) => updateNestedField('aorta.peakSystolicVelocity', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Aortic Waveform:</label>
            <select
              value={formData.aorta.waveform}
              onChange={(e) => updateNestedField('aorta.waveform', e.target.value)}
            >
              {WAVEFORM_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Proximal Aorta (cm):</label>
            <input
              type="number"
              step="0.1"
              placeholder="Measurement"
              value={formData.aorta.proximal}
              onChange={(e) => updateNestedField('aorta.proximal', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Mid Aorta (cm):</label>
            <input
              type="number"
              step="0.1"
              placeholder="Measurement"
              value={formData.aorta.mid}
              onChange={(e) => updateNestedField('aorta.mid', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Distal Aorta (cm):</label>
            <input
              type="number"
              step="0.1"
              placeholder="Measurement"
              value={formData.aorta.distal}
              onChange={(e) => updateNestedField('aorta.distal', e.target.value)}
            />
          </div>
        </div>

        {/* Iliac Vessels */}
        <div className="form-section">
          <h3>Iliac Vessels</h3>
          
          <div className="form-group">
            <label>Right Common Iliac Artery (cm):</label>
            <input
              type="number"
              step="0.1"
              placeholder="Measurement"
              value={formData.iliacVessels.rightIliac}
              onChange={(e) => updateNestedField('iliacVessels.rightIliac', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Left Common Iliac Artery (cm):</label>
            <input
              type="number"
              step="0.1"
              placeholder="Measurement"
              value={formData.iliacVessels.leftIliac}
              onChange={(e) => updateNestedField('iliacVessels.leftIliac', e.target.value)}
            />
          </div>
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={isLoading}
            className="preview-btn"
          >
            Preview Report
          </button>
          <button 
            type="submit" 
            disabled={isLoading} 
            className="submit-btn"
          >
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button 
            type="button"
            onClick={resetForm}
            disabled={isLoading}
            className="reset-btn"
          >
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default AortaForm;
